import { Component, Input, OnChanges } from '@angular/core'
import { Subscription } from 'rxjs'
import { Fee } from 'src/app/models/fee-response.model'
import { Footnote } from 'src/app/models/footnote.model'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'
import { FootnoteService } from 'src/app/services/footnotes/footnotes.service'

const PURCHASE_FEE = 'PURCHASE_FEE'
const REDEMPTION_FEE = 'REDEMPTION_FEE'
const TWELVE_B1_FEE = 'TWELVE_B1_FEE'
const ACCOUNT_SERVICE_FEE = 'ACCOUNT_SERVICE_FEE'
const ONLINE_TRANSACTION_FEE = 'ONLINE_TRANSACTION_FEE'
const PHONE_TRANSACTION_FEE = 'PHONE_TRANSACTION_FEE'
const LOAD_FEE = 'LOAD_FEE'
@Component({
  selector: 'fees',
  templateUrl: './fees.component.html',
  styleUrls: ['./fees.component.scss'],
  providers: [],
})
export class FeesComponent implements OnChanges {
  @Input() profileData: any
  @Input() feeData: Fee[]
  feeType: string
  feeList: any[]
  aemContentSub: Subscription
  transactionFeeContent = ''
  displayVanguardBrokerageRedemptionFeeContent = false
  dialogTitle: string
  dialogBody: string

  disclaimer529ExpenseRatio: AEMChunk
  vanguardBrokerageRedemptionFee: AEMChunk
  aemChunk12b1fee: AEMChunk
  noTransactionFeeContent: AEMChunk
  noTransactionFeeFootnote: Footnote

  constructor(
    private readonly centralContentService: CentralContentService,
    private readonly footnoteService: FootnoteService,
  ) {}

  ngOnInit() {
    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        this.parseAEMContent(content)
      },
    )
  }

  parseAEMContent(content) {
    this.disclaimer529ExpenseRatio =
      this.centralContentService.getContentByBody(
        'fund_definitions_529_expense_ratio',
        content,
      )
    this.vanguardBrokerageRedemptionFee =
      this.centralContentService.getContentByBody(
        'fund_definitions_non_vanguard_brokerage_redem_fee',
        content,
      )
    this.aemChunk12b1fee = this.centralContentService.getContentByBody(
      'fund_definitions_12b1fee',
      content,
    )
    this.noTransactionFeeContent = this.centralContentService.getContentByBody(
      'footnote_no_transaction_fee',
      content,
    )
  }

  ngOnChanges(): void {
    if (this.feeData) {
      this.feeData.forEach((element) => {
        this.feeData[element.FeeType] = {
          content: element.content,
          FeeType: element.FeeType,
          customizedContent: element.customizedContent,
        }
      })
    }

    if (this.profileData?.is529) {
      this.feeType = 'Portfolio'
      this.feeList = this.getFees529()
    } else if (this.profileData?.isExternal) {
      this.feeType = 'Fund'
      this.feeList = this.getFeesNonVanguard()
    } else {
      this.feeType = 'Fund'
      this.feeList = this.getFees()
    }
  }

  getFees() {
    const list = []
    if (this.feeData) {
      this.displayPurchaseFee(list)
      this.displayRedemptionFee(list)
      this.displayBFee(list)
    }
    return list
  }

  getFees529() {
    const list = []
    if (this.feeData) {
      this.displayTransactionFee(list)
      this.displayAccServiceFee(list)
      this.displayRedemptionFee(list)
      this.displayBFee(list)
    }
    return list
  }

  getFeesNonVanguard() {
    const list = []
    if (this.feeData) {
      this.displayPurchaseFee(list)
      this.displayRedemptionFee(list)
      this.displayVanguardBrokerageRedemptionFee()
      this.displayBFee(list)
      this.displayTransactionFee(list)
      this.displayLoadFee(list)
    }
    return list
  }

  displayPurchaseFee(list: any[]) {
    list.push({
      label: 'Purchase fee',
      content: this.feeData[PURCHASE_FEE]?.content || `—`,
    })
  }

  displayBFee(list: any[]) {
    list.push({
      label: '12b-1 fee',
      content: this.feeData[TWELVE_B1_FEE]?.content || `—`,
    })
  }

  displayVanguardBrokerageRedemptionFee() {
    this.displayVanguardBrokerageRedemptionFeeContent = false
    if (this.profileData?.isNTFFund) {
      this.displayVanguardBrokerageRedemptionFeeContent = true
    }
  }

  displayTransactionFee(list: any[]) {
    if (!this.profileData?.isExternal) {
      list.push({
        label: 'Transaction fee',
        content: this.feeData[PHONE_TRANSACTION_FEE]?.content || `—`,
      })
    } else {
      list.push({
        label: 'Transaction fee',
        content: this.formatTransactionFee(),
        note: this.formatTransactionFeeNote(),
      })
    }
  }

  displayAccServiceFee(list: any[]) {
    list.push({
      label: 'Account service fee',
      content: this.feeData[ACCOUNT_SERVICE_FEE]?.content || `—`,
    })
  }
  displayRedemptionFee(list: any[]) {
    const redemptionFeeLabel = 'Fund family redemption fee'

    if (!this.profileData?.isExternal) {
      list.push({
        label: redemptionFeeLabel,
        content: this.feeData[REDEMPTION_FEE]?.content || `—`,
      })
    } else {
      list.push({
        label: redemptionFeeLabel,
        content: this.formatRedemptionFee(
          this.feeData[REDEMPTION_FEE]?.content || `—`,
        ),
      })
    }
  }

  displayLoadFee(list: any[]) {
    const _content = this.feeData[LOAD_FEE]?.content || `—`
    list.push({
      label: 'Load',
      content: _content.replace('**', ''),
    })
  }

  formatTransactionFee() {
    if (
      this.profileData?.isNTFFund ||
      this.feeData[ONLINE_TRANSACTION_FEE]?.content === '$0'
    ) {
      this.transactionFeeContent = 'None'
      this.setNoTransactionFeeFootnote()
    }
    if (!this.profileData?.isNTFFund && !this.profileData?.isLoadFund) {
      this.transactionFeeContent = this.transactionFeeContent.concat(
        'See commissions schedule',
      )
    }
    return this.transactionFeeContent
  }

  setFeeRpaTag(feeLabel: string): string {
    const feeMap = {
      'Purchase fee': 'pf-fsf-purchaseFee',
      'Fund family redemption fee': 'pf-fsf-fundFamilyRedemptionFee',
    }
    return feeMap[feeLabel] || ''
  }

  formatTransactionFeeNote() {
    let transactionFeeNote = ''
    if (this.profileData?.isNTFFund) {
      transactionFeeNote =
        'Note: There may be a broker-assisted fee for trades placed by phone. ' +
        'See the <a href="https://investor.vanguard.com/client-benefits/brokerage-fees-commissions">' +
        'Vanguard brokerage commission and fee schedule</a> for more information.'
    }
    if (this.profileData?.isLoadFund) {
      transactionFeeNote = transactionFeeNote
        .concat(transactionFeeNote ? '<br/>' : '')
        .concat(
          'Note: Online: ' + this.feeData[ONLINE_TRANSACTION_FEE]?.content,
        )
        .concat(
          ', Associate Assistance: ' +
            this.feeData[PHONE_TRANSACTION_FEE]?.content,
        )
    }
    return transactionFeeNote
  }

  formatRedemptionFee(feeValue) {
    if (this.isNoneFeeApplicable() && feeValue === 'None') {
      return 'See prospectus'
    } else {
      return feeValue
    }
  }

  isNoneFeeApplicable() {
    return (
      !this.profileData?.isFundAccessFund ||
      this.profileData?.isFundTransferableToVGI ||
      this.profileData?.isNTFFund
    )
  }

  public setDialogInfo(title) {
    this.dialogTitle = title
    if (title === 'Expense ratio') {
      this.dialogBody = this.disclaimer529ExpenseRatio?.contentValue
    } else if (title === '12b-1 Fee') {
      this.dialogBody = this.aemChunk12b1fee.contentValue
    }
    //what to do if we don't match any titles?
  }

  setNoTransactionFeeFootnote() {
    this.noTransactionFeeFootnote = new Footnote(
      this.noTransactionFeeContent.contentValue,
      'performance',
      this.noTransactionFeeContent.isAEM,
      'footnote_no_transaction_fee',
    )
    this.footnoteService.addFootnote(this.noTransactionFeeFootnote)
  }
}
